import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Download, FileSignature, PenLine } from 'lucide-react';
import { contractsAPI } from '../services/api';
import { formatDate } from '../utils/format';

/**
 * Contracts: the company's agreements, signed and waiting.
 *
 * Signed copies stay here for good, so nobody depends on an old email to
 * find what they agreed to. A contract waiting for the signed-in person's
 * signature opens straight into signing; because they are signed in with
 * that verified address, no emailed code is needed.
 *
 * Customer-facing: names no vendor.
 */
const STATUS = {
  sent: ['Waiting for signatures', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  signed: ['Signed, being countersigned', 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'],
  completed: ['Signed by everyone', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'],
  declined: ['Declined', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'],
  voided: ['Withdrawn', 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400'],
};

export default function ContractsPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => contractsAPI.list().then((r) => r.data.contracts),
  });

  const download = async (c) => {
    setError('');
    try {
      const r = await contractsAPI.signed(c.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${c.title} - signed.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not download the signed copy.');
    }
  };

  const signNow = async (c) => {
    setError('');
    try {
      const r = await contractsAPI.signLink(c.id);
      navigate(r.data.url);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not open the contract.');
    }
  };

  const waiting = (data || []).filter((c) => c.yourTurn);
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2"><FileSignature className="w-6 h-6" /> Contracts</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Agreements between your company and us. Signed copies are kept here.</p>
      </div>

      {error && (
        <div className="flex gap-2 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-200"><AlertCircle className="w-4 h-4 mt-0.5" /> {error}</div>
      )}

      {waiting.length > 0 && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20 space-y-2">
          <h2 className="font-medium text-primary-900 dark:text-primary-100">Waiting for your signature</h2>
          {waiting.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm dark:text-neutral-200">{c.title}</span>
              <button onClick={() => signNow(c)} className="btn-primary btn-sm inline-flex items-center gap-1"><PenLine className="w-3.5 h-3.5" /> Review and sign</button>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/40 text-left text-neutral-500 dark:text-neutral-400">
            <tr>
              <th className="px-4 py-3 font-medium">Contract</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Date</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {isLoading && <tr><td colSpan={4} className="px-4 py-6 text-center text-neutral-500">Loading…</td></tr>}
            {!isLoading && !data?.length && <tr><td colSpan={4} className="px-4 py-10 text-center text-neutral-500 dark:text-neutral-400">No contracts yet.</td></tr>}
            {data?.map((c) => {
              const [label, cls] = c.expired ? ['Link expired', STATUS.declined[1]] : STATUS[c.status] || [c.status, ''];
              return (
                <tr key={c.id}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-neutral-900 dark:text-white">{c.title}</div>
                    <div className="text-xs text-neutral-500">
                      {c.company_name ? `${c.company_name} · ` : ''}{c.signers.filter((s) => s.role === 'signer').map((s) => `${s.signed ? '✓ ' : ''}${s.name}`).join(', ')}
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span></td>
                  <td className="px-4 py-3 text-neutral-500">{formatDate(c.completed_at || c.sent_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {c.status === 'completed' && (
                      <button onClick={() => download(c)} className="text-primary-600 hover:underline inline-flex items-center gap-1"><Download className="w-4 h-4" /> Signed copy</button>
                    )}
                    {c.yourTurn && <button onClick={() => signNow(c)} className="text-primary-600 hover:underline">Sign</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
